import { connect, useDispatch } from 'react-redux';
import { ARRAY_ERROR } from 'final-form';
import { FormSpy } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { Dispatch, useCallback } from 'react';

import { IKnmReasonTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmReasonTypeSelectOption';
import { getReasonBlockId, REASONS_TITLE_BLOCK_ID } from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmErpForm } from 'KnmErpPackage/MainModule/interfaces/IKnmErpForm';
import {
  getKnmErpReasonTypesSelectOptions,
  getKnmFormRef,
  getKnmOrdersSelectOptions,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { ErknmLinks } from 'KnmErknmPackage/ErknmEventSubjectModule/components/Links/ErknmLinks';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { isAssignmentRequiredByType } from 'KnmPackage/MainModule/common/isAssignmentRequiredByType';
import { IKnmOrderSelectOption } from 'KnmPackage/MainModule/interfaces/IKnmOrderSelectOption';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { catalogOrdersRelationDictionariesGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { getFullNameGuarantor } from 'KnmPmPackage/PmReasonsModule/common/getFullNameGuarantor';
import { knmGetOrders } from 'KnmPackage/MainModule/actions/knmActions';
import { IDictionaryVersion } from 'APIPackage/DictionariesModule/interfaces/IDictionaryVersion';
import { fetchDictionaryValue } from 'APIPackage/DictionariesModule/services/dictionariesApi';
import { DictionaryUUIDsEnum } from 'CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import styles from './KnmErpReasons.module.scss';
import { IKnmErpErvkFormObject } from '../../KnmErpSubjectModule/interfaces/IKnmErpErvkFormObject';
import {
  approveRequiredSelectOption,
  notApproveRequiredSelectOption,
  notificationApproveRequiredSelectOption,
} from '../../../KnmErknmPackage/MainModule/constants/approveRequiredSelectOptions';
import { IApproveRequiredSelectOption } from '../../../KnmErknmPackage/MainModule/interfaces/IApproveRequiredSelectOption';
import { KnmReasonRiskIndicators } from '../../../KnmPackage/KnmReasonRiskIndicatorModule/components/KnmReasonRiskIndicators';
import { isOneReasonLinkRequired } from '../../../KnmPackage/MainModule/common/isOneReasonLinkRequired';
import { IKnmErpFormErvkReason } from '../interfaces/IKnmErpFormErvkReason';
import { ErknmReasonProgramDocument } from '../../../KnmErknmPackage/ErknmReasonsModule/components/ErknmReasonProgramDocument';
import { IKnmErknmForm } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmForm';

interface IKnmErpReasonsErvkProps {
  watchOnly?: boolean;
  formRef: IKnmFormRef | undefined;
  knmErpReasonTypesSelectOptions: IKnmReasonTypeSelectOption[] | undefined;
  isSupervisor: boolean;
  knmOrders: IKnmOrderSelectOption[] | undefined;
}

const KnmErpReasonsErvkWithoutStore = ({
  watchOnly,
  formRef,
  knmErpReasonTypesSelectOptions,
  isSupervisor,
  knmOrders,
}: IKnmErpReasonsErvkProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const reasonsFieldName = 'reasonsErvk';

  /**
   * Обработчик изменения основания проведения
   */
  const onChangeReason = useCallback(
    async (value: IKnmReasonTypeSelectOption) => {
      if (value.ervkUuid && typeof value?.agreement === 'string') {
        let option: IApproveRequiredSelectOption | undefined;

        if (value.agreement === notificationApproveRequiredSelectOption.agreementValue) {
          option = notificationApproveRequiredSelectOption;
        } else if (value.agreement === notApproveRequiredSelectOption.agreementValue) {
          option = notApproveRequiredSelectOption;
        } else if (value.agreement === approveRequiredSelectOption.agreementValue) {
          option = approveRequiredSelectOption;
        }

        formRef?.current.change('approveRequired', option);
      } else {
        formRef?.current.change('approveRequired');
      }
    },
    [formRef],
  );

  const handleReasonTypeChange = (index: number) => async (value: IKnmReasonTypeSelectOption) => {
    formRef?.current.change(`${reasonsFieldName}[${index}].reason` as keyof IKnmErpForm);
    formRef?.current.change(`${reasonsFieldName}[${index}].ervkUuid` as keyof IKnmErpForm);
    formRef?.current.change(`${reasonsFieldName}[${index}].title` as keyof IKnmErpForm);
    formRef?.current.change(`${reasonsFieldName}[${index}].shortTitle` as keyof IKnmErpForm);
    formRef?.current.change(`${reasonsFieldName}[${index}].text` as keyof IKnmErpForm);

    if (value) {
      await onChangeReason(value);
    }

    formRef?.current.change(`${reasonsFieldName}[${index}].requirementDetails` as keyof IKnmErpForm);
    formRef?.current.change(`${reasonsFieldName}[${index}].dateRequirement` as keyof IKnmErpForm);
    formRef?.current.change(`${reasonsFieldName}[${index}].assignmentNumber` as keyof IKnmErpForm);
    formRef?.current.change(`${reasonsFieldName}[${index}].assignmentDate` as keyof IKnmErpForm);
    formRef?.current.change(`${reasonsFieldName}[${index}].requirementDetails` as keyof IKnmErpForm);
    formRef?.current.change(`${reasonsFieldName}[${index}].riskIndicators` as keyof IKnmErpForm);
    formRef?.current.change(`${reasonsFieldName}[${index}].order` as keyof IKnmErpForm);
    formRef?.current.change(`${reasonsFieldName}[${index}].fullName` as keyof IKnmErpForm);
    formRef?.current.change(`${reasonsFieldName}[${index}].indicatorRiskAttachment` as keyof IKnmErpForm);
    formRef?.current.change(`${reasonsFieldName}[${index}].indicatorRiskAuto` as keyof IKnmErpForm);
    formRef?.current.change(`${reasonsFieldName}[${index}].links` as keyof IKnmErpForm);
    formRef?.current.change(`${reasonsFieldName}[${index}].programAttachment` as keyof IKnmErpForm);
    formRef?.current.change(`${reasonsFieldName}[${index}].programNumber` as keyof IKnmErpForm);

    if (value.ervkUuid) {
      const dictionaryValue: IDictionaryVersion = await fetchDictionaryValue(DictionaryUUIDsEnum.ERVK, value.ervkUuid);

      const reasonRequirementsValue = JSON.parse(dictionaryValue.value);

      formRef?.current.change(`reasonRequirements` as keyof IKnmErpForm, reasonRequirementsValue);
      formRef?.current.change(`${reasonsFieldName}[${index}].ervkUuid` as keyof IKnmErpForm, value?.ervkUuid);
    }

    // @ts-ignore
    const objects: IKnmErpErvkFormObject[] | undefined = formRef?.current.getFieldState(
      'objectsErvk' as keyof IKnmErpForm,
    )?.value;

    objects?.forEach((item, i) => {
      formRef?.current.change(`objectsErvk[${i}].objectType` as keyof IKnmErpForm);
      formRef?.current.change(`objectsErvk[${i}].objectKind` as keyof IKnmErpForm);
      formRef?.current.change(`objectsErvk[${i}].objectSubKind` as keyof IKnmErpForm);
      formRef?.current.change(`objectsErvk[${i}].riskCategory` as keyof IKnmErpForm);
      formRef?.current.change(`objectsErvk[${i}].dangerClass` as keyof IKnmErpForm);
    });

    // Затирания нижестоящих по логике заполнения полей при изменении вышестоящих
    if (!watchOnly) {
      formRef?.current.change('kindErvk', undefined);
      formRef?.current.change('subjectTypeErvk', undefined);
    }
  };

  const isReasonFieldsDisabled = !(isSupervisor && !watchOnly);

  const addReasonEvent = useCallback(
    () => !watchOnly && push && push(reasonsFieldName, { approveRequired: true, listId: uuidv4() }),
    [push, watchOnly],
  );

  const makeRemoveReasonEventHandler = (index: number) => () => !watchOnly && remove && remove(reasonsFieldName, index);

  const clearReasonCallback = useCallback(
    (fieldName: string) => {
      formRef?.current.change('approveRequired');
      // @ts-ignore
      formRef?.current.change(`${fieldName}`);
    },
    [formRef],
  );

  const handleOrderChange = (index: number) => (value?: IKnmOrderSelectOption) => {
    const { number, date, guarantors, recordId, recordVersionId } = value || {};

    dispatch(catalogOrdersRelationDictionariesGet(recordId, recordVersionId));

    formRef?.current.change('organizationAutocomplete', false);
    formRef?.current.change('subjectTypeErvk');
    formRef?.current.change('inn');
    formRef?.current.change('snils');
    formRef?.current.change('organizationName');
    formRef?.current.change('ogrn');

    formRef?.current.change('objectsErvk');

    formRef?.current.change(`${reasonsFieldName}[${index}].assignmentNumber` as keyof IKnmErknmForm, number);
    formRef?.current.change(`${reasonsFieldName}[${index}].assignmentDate` as keyof IKnmErknmForm, date);
    formRef?.current.change(
      `${reasonsFieldName}[${index}].fullName` as keyof IKnmErknmForm,
      getFullNameGuarantor(guarantors?.[0]),
    );
  };

  const getOrderOptions = (reasonFieldValue: IKnmErpFormErvkReason) => () => {
    const { knoOrganizationErvk, supervisionTypeErvk, typeErvk } = formRef?.current.getState().values ?? {};

    if (knoOrganizationErvk && supervisionTypeErvk && typeErvk) {
      dispatch(
        knmGetOrders(
          String(knoOrganizationErvk.value.dictVersionId),
          String(supervisionTypeErvk.value.dictVersionId),
          String(reasonFieldValue.type?.ervkUuid),
          false,
          typeErvk.code,
        ),
      );
    }
  };

  const makeRemoveReasonOrder = (index: number) => () => {
    if (!watchOnly) {
      formRef?.current.change(`${reasonsFieldName}[${index}].order` as keyof IKnmErpForm);
      handleOrderChange(index)();
    }
  };

  return (
    <FieldBlock
      label="Основания регистрации КНМ"
      id={REASONS_TITLE_BLOCK_ID}
      button={
        <Button
          className="shared-btn-add"
          text="Добавить"
          variant="link"
          color="primary"
          type="button"
          disabled={watchOnly}
          onClick={addReasonEvent}
        />
      }
    >
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          const reasonsFieldError = props.errors?.reasons?.[ARRAY_ERROR];
          const isReasonsFieldTouched = props.touched?.reasons;

          if (reasonsFieldError && isReasonsFieldTouched) {
            return <p className="shared-input-error">{reasonsFieldError}</p>;
          }

          return null;
        }}
      </FormSpy>
      <Row>
        <FieldArray name={reasonsFieldName}>
          {({ fields }) => {
            if (!fields.length) {
              return null;
            }

            return fields.map((name, index) => {
              const reasonFieldValue: IKnmErpFormErvkReason = fields.value?.[index];
              const id = getReasonBlockId(reasonFieldValue?.listId);
              const { order } = reasonFieldValue;
              const isActiveOrderHelpText = order?.flagInagency || order?.flagIt || order?.flagMsp || order?.flagSonko;

              return (
                <Row key={id} id={id} isListItem margin="medium">
                  <div className="shared-deleting-row">
                    <SelectInputField
                      disabled={isReasonFieldsDisabled}
                      id={`${name}.type`}
                      isClearable={!isReasonFieldsDisabled && !!reasonFieldValue?.type}
                      isSearchable
                      isShowTooltip={true}
                      name={`${name}.type`}
                      onChange={handleReasonTypeChange(index)}
                      onClear={() =>
                        !isReasonFieldsDisabled && clearReasonCallback && clearReasonCallback(`${name}.type`)
                      }
                      options={knmErpReasonTypesSelectOptions}
                      placeholder="Выберите основание регистрации КНМ"
                      size="medium"
                      tooltipWidth={document.getElementById(`${name}.type`)?.offsetWidth}
                      validate={makeRequired('Поле не может быть пустым')}
                      className={styles.ReasonSelectorField}
                    />

                    {reasonFieldValue?.type?.hasText && (
                      <FieldBlock label="Дополнительно" color="black" margin="top">
                        <TextareaField disabled={isReasonFieldsDisabled} id={`${name}.text`} name={`${name}.text`} />
                      </FieldBlock>
                    )}
                    {reasonFieldValue?.type?.indicatorRiskRequired && (
                      <KnmReasonRiskIndicators
                        isErp={true}
                        formRef={formRef}
                        isWatchOnly={isReasonFieldsDisabled}
                        reasonFieldValue={reasonFieldValue}
                        reasonsFieldName={name}
                      />
                    )}
                    {(reasonFieldValue?.type?.requirementDetailsRequired ||
                      reasonFieldValue?.type?.detailsRequirement) &&
                      reasonFieldValue?.type?.dateRequirement && (
                        <Row notWrap>
                          <FieldBlock label="Дата требования" placementLabel="left" color="black" type="not-space">
                            <DatePickerField
                              className={styles.ReasonDateField}
                              disabled={isReasonFieldsDisabled}
                              id={`${name}.dateRequirement`}
                              name={`${name}.dateRequirement`}
                              placeholder="Выберите дату"
                              validate={makeRequired('Поле не может быть пустым')}
                            />
                          </FieldBlock>
                          <FieldBlock label="Реквизиты требования" placementLabel="left" color="black" size="half">
                            <TextareaField
                              className="shared-row-field"
                              disabled={isReasonFieldsDisabled}
                              id={`${name}.requirementDetails`}
                              name={`${name}.requirementDetails`}
                            />
                          </FieldBlock>
                        </Row>
                      )}
                    {!reasonFieldValue?.type?.assignmentNumberRequired &&
                      !reasonFieldValue?.type?.approveRequired &&
                      reasonFieldValue?.type?.dateRequired && (
                        <FieldBlock size="half" label="Дата" placementLabel="left" color="black">
                          <DatePickerField
                            className="shared-row-field"
                            disabled={watchOnly}
                            id={`${name}.date`}
                            name={`${name}.date`}
                          />
                        </FieldBlock>
                      )}

                    {isAssignmentRequiredByType(reasonFieldValue?.type) && (
                      <FieldBlock margin="top">
                        <div className="shared-field-row">
                          <div className="shared-row-flex">
                            {!order && (
                              <Row>
                                <FieldBlock
                                  label="Выберите поручение"
                                  placementLabel="left"
                                  color="black"
                                  labelSize="col-4"
                                >
                                  <SelectInputField
                                    className="shared-row-field"
                                    disabled={isReasonFieldsDisabled}
                                    id={`${name}.order`}
                                    isSearchable
                                    name={`${name}.order`}
                                    options={knmOrders}
                                    onChange={handleOrderChange(index)}
                                    onOpen={getOrderOptions(reasonFieldValue)}
                                    placeholder="Введите номер, дату или полное наименование поручения"
                                    size="medium"
                                  />
                                </FieldBlock>
                                {isActiveOrderHelpText && (
                                  <div className="shared-help-text">
                                    Статус лица в реестре противоречит требованиям поручения
                                  </div>
                                )}
                              </Row>
                            )}
                            {reasonFieldValue && !!reasonFieldValue.order && (
                              <>
                                <Row>
                                  <FieldBlock
                                    size="half"
                                    label="Номер поручения"
                                    placementLabel="left"
                                    color="black"
                                    margin="right"
                                  >
                                    <TextInputField
                                      className="shared-row-field"
                                      id={`${name}.assignmentNumber`}
                                      name={`${name}.assignmentNumber`}
                                      disabled={true}
                                    />
                                  </FieldBlock>
                                  <FieldBlock size="half" label="Дата поручения" placementLabel="left" color="black">
                                    <TextInputField
                                      className="shared-row-field"
                                      id={`${name}.assignmentDate`}
                                      name={`${name}.assignmentDate`}
                                      disabled={true}
                                    />
                                  </FieldBlock>
                                </Row>
                                <Row>
                                  <FieldBlock
                                    label="ФИО лица, издавшего поручение"
                                    color="black"
                                    placementLabel="left"
                                    margin="top"
                                    labelSize="col-4"
                                  >
                                    <TextareaField
                                      resize="none"
                                      className="shared-row-field"
                                      name={`${name}.fullName`}
                                      disabled={true}
                                    />
                                  </FieldBlock>
                                </Row>
                              </>
                            )}
                          </div>
                          {order && (
                            <CloseButton
                              className="shared-deleting-row-btn"
                              disabled={isReasonFieldsDisabled}
                              onClick={makeRemoveReasonOrder(index)}
                            />
                          )}
                        </div>
                      </FieldBlock>
                    )}
                    {reasonFieldValue?.type?.lastConductedKnmDateRequired && (
                      <div className={styles.ReasonDateFieldWrapper}>
                        <Row>
                          <FieldBlock size="half" label="Дата" placementLabel="left" color="black">
                            <DatePickerField
                              className="shared-row-field"
                              id={`${name}.lastConductedKnmDate`}
                              name={`${name}.lastConductedKnmDate`}
                              placeholder="Выберите дату"
                              disabled={isReasonFieldsDisabled}
                            />
                          </FieldBlock>
                        </Row>
                      </div>
                    )}
                    {reasonFieldValue?.type?.linkOther && (
                      <ErknmLinks
                        isAddDisabled={
                          isOneReasonLinkRequired(reasonFieldValue.type) && !!reasonFieldValue.links?.length
                        }
                        isEnabled={true}
                        linksName={`${name}.links`}
                        reasonsFieldName={`${name}`}
                        watchOnly={isReasonFieldsDisabled}
                        reason={reasonFieldValue}
                        isErp
                      />
                    )}
                    {reasonFieldValue?.type?.name === KnmReasonTypeNameEnum.CHECKING_PROGRAM && (
                      <FieldBlock margin="top">
                        <div className="shared-field-row">
                          <div className="shared-row-flex">
                            {reasonFieldValue?.type?.programNumberRequired && (
                              <Row>
                                <FieldBlock label="Номер программы проверок" placementLabel="left" color="black">
                                  <TextInputField
                                    className="shared-row-field"
                                    id={`${name}.programNumber`}
                                    name={`${name}.programNumber`}
                                    disabled={isReasonFieldsDisabled}
                                  />
                                </FieldBlock>
                              </Row>
                            )}
                            {reasonFieldValue?.type?.programFileRequired && (
                              <ErknmReasonProgramDocument
                                watchOnly={isReasonFieldsDisabled}
                                name={`${name}.programAttachment`}
                                programAttachment={reasonFieldValue?.programAttachment}
                                formRef={formRef}
                              />
                            )}
                          </div>
                        </div>
                      </FieldBlock>
                    )}
                  </div>
                  {!isReasonFieldsDisabled && (
                    <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveReasonEventHandler(index)} />
                  )}
                </Row>
              );
            });
          }}
        </FieldArray>
      </Row>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState) => ({
  formRef: getKnmFormRef(state),
  knmErpReasonTypesSelectOptions: getKnmErpReasonTypesSelectOptions(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  knmOrders: getKnmOrdersSelectOptions(state),
});

export const KnmErpReasonsErvk = connect(mapStateToProps)(KnmErpReasonsErvkWithoutStore);
